import React from "react";

// Small schematic drawings for the "Example" boxes of the general scenario
// settings (orientation, ducts, dimensions, Space Oriented options).
// Each mini panel is 96 x 70 units; diagrams show 2 or 3 panels side by side.

const C = {
  plate: "#eef1f2",
  edge: "#c9d0d3",
  duct: "#5c9495",
  ductPale: "#c6d6d7",
  a: "#7fa7c4",
  b: "#d6a26a",
  c: "#a7b86b",
  reserve: "#ffffff",
  text: "#5f6b72",
  mark: "#c0392b",
};

function Panel({ x, caption, children }) {
  return (
    <g transform={`translate(${x},0)`}>
      <rect x="0" y="0" width="96" height="70" rx="3" fill={C.plate} stroke={C.edge} />
      {children}
      <text x="48" y="82" textAnchor="middle" fontSize="9" fill={C.text}>{caption}</text>
    </g>
  );
}

function Diagram({ panels = 2, label, children }) {
  const width = panels * 104 - 4;
  return (
    <svg viewBox={`0 0 ${width} 88`} className="ssp-diagram" role="img" aria-label={label}
         style={{ maxWidth: panels === 3 ? 440 : 300 }}>
      {children}
    </svg>
  );
}

const Box = ({ x, y, w, h, fill }) => <rect x={x} y={y} width={w} height={h} rx="1" fill={fill} />;
const DuctH = ({ y, x = 4, w = 88, fill = C.duct }) => <rect x={x} y={y} width={w} height={3} fill={fill} />;
const DuctV = ({ x, y = 4, h, fill = C.duct }) => <rect x={x} y={y} width={3} height={h} fill={fill} />;

// A row of n small devices
const Row = ({ x, y, n, fill, w = 7, h = 10 }) => (
  <g>{Array.from({ length: n }, (_, i) => <Box key={i} x={x + i * (w + 1)} y={y} w={w} h={h} fill={fill} />)}</g>
);

const Note = ({ x, y, children, color = C.text }) => (
  <text x={x} y={y} fontSize="7" fill={color} textAnchor="middle">{children}</text>
);

// ── Orientation ──────────────────────────────────────────────────────────

/** The same three sections (A, B, C) in the three orientations */
export function OrientationDiagram() {
  return (
    <Diagram panels={3} label="Section Mode: each section in its own rows. Space Mode: the next section fills the end of a row. Intelligent Mode: sections side by side in one band, terminals on vertical rails.">
      <Panel x={2} caption="Section Mode">
        <Row x={6} y={5} n={6} fill={C.a} />
        <DuctH y={17} />
        <Row x={6} y={23} n={3} fill={C.b} />
        <DuctH y={35} />
        <Row x={6} y={41} n={5} fill={C.c} w={4} />
        <DuctH y={53} />
      </Panel>
      <Panel x={106} caption="Space Mode">
        <Row x={6} y={5} n={6} fill={C.a} />
        <DuctV x={55} h={12} />
        <Row x={61} y={5} n={3} fill={C.b} />
        <DuctH y={17} />
        <Row x={6} y={23} n={5} fill={C.c} w={4} />
        <DuctH y={35} />
      </Panel>
      <Panel x={210} caption="Intelligent Mode">
        <Row x={6} y={5} n={3} fill={C.a} />
        <Row x={6} y={18} n={3} fill={C.a} />
        <DuctV x={32} h={36} />
        <Row x={38} y={5} n={3} fill={C.b} />
        <DuctV x={63} h={36} />
        {Array.from({ length: 11 }, (_, i) => <rect key={i} x={70} y={5 + i * 3} width={8} height={2.4} fill={C.c} />)}
        <DuctH y={41} />
      </Panel>
    </Diagram>
  );
}

// ── Ducts ────────────────────────────────────────────────────────────────

/** Which ducts each selection controls (highlighted) */
export function DuctsDiagram() {
  const plateDucts = (strong) => (
    <>
      <DuctV x={4} y={4} h={62} fill={strong ? C.duct : C.ductPale} />
      <DuctV x={89} y={4} h={62} fill={strong ? C.duct : C.ductPale} />
      <DuctH y={4} fill={strong ? C.duct : C.ductPale} />
    </>
  );
  const sectionDucts = (strong) => (
    <>
      <DuctH x={7} w={82} y={24} fill={strong ? C.duct : C.ductPale} />
      <DuctH x={7} w={82} y={44} fill={strong ? C.duct : C.ductPale} />
    </>
  );
  return (
    <Diagram label="Enclosure ducts: left, right and top of the plate. Section ducts: the horizontal ducts under each row.">
      <Panel x={2} caption="Enclosure duct">
        {plateDucts(true)}
        {sectionDucts(false)}
        <Row x={10} y={11} n={6} fill={C.a} />
        <Row x={10} y={31} n={4} fill={C.b} />
      </Panel>
      <Panel x={106} caption="Section duct">
        {plateDucts(false)}
        {sectionDucts(true)}
        <Row x={10} y={11} n={6} fill={C.a} />
        <Row x={10} y={31} n={4} fill={C.b} />
      </Panel>
    </Diagram>
  );
}

// ── Dimensions ───────────────────────────────────────────────────────────

/** Tolerance: 0 % fills the row, 10 % keeps a reserve at the end */
export function ToleranceDiagram() {
  return (
    <Diagram label="0 percent: the row is filled to the end. 10 percent: the end of the row stays free.">
      <Panel x={2} caption="0 % – row filled">
        <Row x={6} y={20} n={10} fill={C.a} />
        <DuctH y={32} />
      </Panel>
      <Panel x={106} caption="10 % – reserve kept free">
        <Row x={6} y={20} n={9} fill={C.a} />
        <rect x={79} y={20} width={13} height={10} fill={C.reserve} stroke={C.edge} strokeDasharray="2 1.5" />
        <Note x={85.5} y={42}>reserve</Note>
        <DuctH y={32} />
      </Panel>
    </Diagram>
  );
}

/** Fill mounting space 200 mm: a 150 mm gap stays empty, a 250 mm gap is filled */
export function FillSpaceDiagram() {
  return (
    <Diagram label="Gap smaller than the limit: stays empty. Gap at least the limit: the next section is placed there.">
      <Panel x={2} caption="gap 150 mm < 200: empty">
        <Row x={6} y={10} n={8} fill={C.a} />
        <rect x={70} y={10} width={22} height={10} fill={C.reserve} stroke={C.edge} strokeDasharray="2 1.5" />
        <DuctH y={22} />
        <Row x={6} y={28} n={3} fill={C.b} />
        <DuctH y={40} />
      </Panel>
      <Panel x={106} caption="gap 250 mm ≥ 200: filled">
        <Row x={6} y={10} n={6} fill={C.a} />
        <DuctV x={55} y={8} h={14} />
        <Row x={61} y={10} n={3} fill={C.b} />
        <DuctH y={22} />
      </Panel>
    </Diagram>
  );
}

/** Min distance to wire duct: free space between a row and the duct below it */
export function MinDistanceDiagram() {
  return (
    <Diagram label="0 millimetres: the duct directly between the rows. 10 millimetres: extra space around the duct.">
      <Panel x={2} caption="0 mm">
        <Row x={6} y={12} n={8} fill={C.a} />
        <DuctH y={23} />
        <Row x={6} y={27} n={8} fill={C.a} />
        <DuctH y={39} />
      </Panel>
      <Panel x={106} caption="10 mm – extra gap">
        <Row x={6} y={8} n={8} fill={C.a} />
        <DuctH y={24} />
        <Row x={6} y={33} n={8} fill={C.a} />
        <line x1={78} y1={18.5} x2={78} y2={23.5} stroke={C.mark} strokeWidth="0.8" />
        <line x1={78} y1={27.5} x2={78} y2={32.5} stroke={C.mark} strokeWidth="0.8" />
        <DuctH y={45} />
      </Panel>
    </Diagram>
  );
}

// ── Space Oriented options ───────────────────────────────────────────────

/** Sub-cabinet fill: off = next section on a new row, on = in the free end of the row */
export function SubCabinetFillDiagram() {
  return (
    <Diagram label="Off: the next section starts a new row. On: it fills the free end of the current row, behind a vertical duct.">
      <Panel x={2} caption="Off">
        <Row x={6} y={8} n={6} fill={C.a} />
        <DuctH y={20} />
        <Row x={6} y={26} n={3} fill={C.b} />
        <DuctH y={38} />
      </Panel>
      <Panel x={106} caption="On">
        <Row x={6} y={8} n={6} fill={C.a} />
        <DuctV x={55} y={6} h={14} />
        <Note x={56.5} y={30} color={C.mark}>separator</Note>
        <Row x={61} y={8} n={3} fill={C.b} />
        <DuctH y={20} />
      </Panel>
    </Diagram>
  );
}
